import { useState, useEffect, useCallback, useRef } from 'react';
import toast from 'react-hot-toast';
import { bikeAPI } from '../../api';
import { Badge, Spinner, EmptyState, Modal } from '../../components/common/Misc';
import Button from '../../components/common/Button';
import Input  from '../../components/common/Input';
import styles from './AdminDashboard.module.css';
import imgStyles from './ImageUpload.module.css';

const EMPTY_FORM = { name: '', brand: '', type: 'petrol-scooter', description: '', price_per_day: '', location: '', status: 'available' };
const TYPES = ['petrol-scooter', 'e-scooter', 'petrol-bike', 'premium-bike'];
const TYPE_LABELS = {
  'petrol-scooter': '🛵 Petrol Scooter',
  'e-scooter':      '⚡ E-Scooter',
  'petrol-bike':    '🏍️ Petrol Bike',
  'premium-bike':   '🏆 Premium Bike',
};
const API_BASE = process.env.REACT_APP_API_URL?.replace('/api', '') || '';
// Handle both full URLs (new uploads) and legacy relative paths
const resolveImg = (url) => (url && url.startsWith('http') ? url : `${API_BASE}${url}`);

const AdminBikes = () => {
  const [bikes,    setBikes]    = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [modal,    setModal]    = useState(null); // null | 'create' | bike object
  const [saving,   setSaving]   = useState(false);
  const [form,     setForm]     = useState(EMPTY_FORM);
  const [deleting, setDeleting] = useState(null);

  // Image upload state
  const [selectedFiles,  setSelectedFiles]  = useState([]); // File objects (pre-upload previews)
  const [uploadingImgs,  setUploadingImgs]  = useState(false);
  const [deletingImg,    setDeletingImg]    = useState(null); // filename being deleted
  const [dragOver,       setDragOver]       = useState(false);
  const [savedImages,    setSavedImages]    = useState([]);
  const fileInputRef = useRef(null);

  const fetchBikes = useCallback(async () => {
    setLoading(true);
    try {
      const res = await bikeAPI.getAll({ limit: 50 });
      setBikes(res.data.data);
    } finally { setLoading(false); }
  }, []);

  useEffect(() => { fetchBikes(); }, [fetchBikes]);

  const openCreate = () => {
    setForm(EMPTY_FORM);
    setSelectedFiles([]);
    setSavedImages([]);
    setModal('create');
  };

  const openEdit = (bike) => {
    setForm({ ...bike });
    setSelectedFiles([]);
    setSavedImages(bike.images || []);
    setModal(bike);
  };

  const closeModal = () => {
    setModal(null);
    setSelectedFiles([]);
    setSavedImages([]);
  };

  //  File selection helpers 
  const addFiles = (incoming) => {
    const existingCount = modal !== 'create' ? (modal?.images?.length || 0) : 0;
    const slots = 5 - existingCount - selectedFiles.length;
    if (slots <= 0) { toast.error('Maximum 5 images per bike'); return; }

    const allowed  = Array.from(incoming).filter(f => f.type.match(/image\/(jpeg|png|webp)/));
    const rejected = Array.from(incoming).filter(f => !f.type.match(/image\/(jpeg|png|webp)/));
    if (rejected.length) toast.error('Only JPEG, PNG and WebP allowed');

    const tooBig = allowed.filter(f => f.size > 5 * 1024 * 1024);
    if (tooBig.length) { toast.error('Each image must be under 5 MB'); return; }

    const accepted = allowed.slice(0, slots);
    if (accepted.length < allowed.length) toast.error(`Only ${slots} slot(s) remaining  extra files skipped`);

    // Attach preview URL
    const withPreviews = accepted.map(f => Object.assign(f, { preview: URL.createObjectURL(f) }));
    setSelectedFiles(prev => [...prev, ...withPreviews]);
  };

  const removeSelected = (index) => {
    setSelectedFiles(prev => {
      URL.revokeObjectURL(prev[index].preview);
      return prev.filter((_, i) => i !== index);
    });
  };

  const handleDrop = (e) => {
    e.preventDefault();
    setDragOver(false);
    addFiles(e.dataTransfer.files);
  };

  //  Save bike (create / update) then upload pending images 
  const handleSave = async () => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        price_per_day: Number(form.price_per_day),
      };

      let savedBike;
      if (modal === 'create') {
        const res = await bikeAPI.create(payload);
        savedBike = res.data.data;
      } else {
        const res = await bikeAPI.update(modal._id, payload);
        savedBike = res.data.data;
      }

      // Upload any pending images
      if (selectedFiles.length > 0) {
        setUploadingImgs(true);
        const formData = new FormData();
        selectedFiles.forEach(f => formData.append('images', f));
        try {
          const uploadRes = await bikeAPI.uploadImages(savedBike._id, formData);
          savedBike = uploadRes.data.data;
          setSavedImages(savedBike.images || []);
          selectedFiles.forEach(f => URL.revokeObjectURL(f.preview));
          setSelectedFiles([]);
        } catch (uploadErr) {
          toast.error(`Bike saved but images failed: ${uploadErr.response?.data?.message || 'Upload error'}`);
        } finally {
          setUploadingImgs(false);
        }
      }

      toast.success(modal === 'create' ? 'Bike added!' : 'Bike updated!');
      closeModal();
      fetchBikes();
    } catch (err) {
      toast.error(err.response?.data?.message || 'Save failed');
    } finally {
      setSaving(false);
    }
  };

  //  Delete an already-saved image 
  const handleDeleteSavedImage = async (bikeId, imageUrl) => {
    const filename = imageUrl.split('/').pop();
    setDeletingImg(filename);
    // Optimistically remove from UI immediately
    setSavedImages(prev => prev.filter(img => img !== imageUrl));
    try {
      await bikeAPI.deleteImage(bikeId, filename);
      toast.success('Image removed');
    } catch (err) {
      // Rollback on failure
      setSavedImages(prev => [...prev, imageUrl]);
      toast.error(err.response?.data?.message || 'Failed to delete image');
    } finally {
      setDeletingImg(null);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this bike from the fleet?')) return;
    setDeleting(id);
    try { await bikeAPI.delete(id); toast.success('Bike removed'); fetchBikes(); }
    catch { toast.error('Delete failed'); }
    finally { setDeleting(null); }
  };

  const handleStatusChange = async (id, status) => {
    try { await bikeAPI.updateStatus(id, { status }); toast.success('Status updated'); fetchBikes(); }
    catch { toast.error('Update failed'); }
  };

  const set = (k) => (e) => setForm(f => ({ ...f, [k]: e.target.value }));

  // Total images in the edit modal (saved + pending)
  const totalImgCount = savedImages.length + selectedFiles.length;
  const slotsLeft     = 5 - totalImgCount;

  return (
    <div>
      <div className={styles.sectionHeader}>
        <h2 className={styles.sectionTitle}>Fleet management</h2>
        <Button onClick={openCreate}>+ Add bike</Button>
      </div>

      {loading ? <Spinner /> : bikes.length === 0
        ? <EmptyState icon="🚲" title="No bikes yet" subtitle="Add your first bike to the fleet" />
        : (
          <div className={styles.tableWrap}>
            <table>
              <thead>
                <tr><th>Image</th><th>Name</th><th>Brand</th><th>Type</th><th>₹/day</th><th>Location</th><th>Status</th><th>Rating</th><th>Actions</th></tr>
              </thead>
              <tbody>
                {bikes.map(bike => (
                  <tr key={bike._id}>
                    <td>
                      <div className={imgStyles.tableThumb}>
                        {bike.images?.[0]
                          ? <img src={resolveImg(bike.images[0])} alt={bike.name} />
                          : <span>🚲</span>
                        }
                      </div>
                    </td>
                    <td><strong>{bike.name}</strong></td>
                    <td>{bike.brand}</td>
                    <td style={{ textTransform: 'capitalize' }}>{bike.type}</td>
                    <td>₹{bike.price_per_day}</td>
                    <td>{bike.location}</td>
                    <td>
                      <select
                        value={bike.status}
                        onChange={e => handleStatusChange(bike._id, e.target.value)}
                        style={{ fontSize: '.8rem', padding: '.2rem .4rem', borderRadius: 4, border: '1px solid var(--color-border)' }}
                      >
                        {['available','booked','maintenance','retired'].map(s => <option key={s} value={s}>{s}</option>)}
                      </select>
                    </td>
                    <td>{bike.avg_rating > 0 ? `⭐ ${bike.avg_rating}` : ''}</td>
                    <td>
                      <div className={styles.actionBtns}>
                        <Button size="sm" variant="secondary" onClick={() => openEdit(bike)}>Edit</Button>
                        <Button size="sm" variant="danger" loading={deleting === bike._id} onClick={() => handleDelete(bike._id)}>Del</Button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      }

      {modal && (
        <Modal title={modal === 'create' ? 'Add new bike' : `Edit  ${modal.name}`} onClose={closeModal}>
          <div className={styles.formGrid}>
            {/* Text fields */}
            <div className={styles.formRow}>
              <Input label="Name"  value={form.name}  onChange={set('name')}  placeholder="Trail Blazer X1" />
              <Input label="Brand" value={form.brand} onChange={set('brand')} placeholder="Trek" />
            </div>
            <div className={styles.formRow}>
              <div>
                <label style={{ fontSize: '.85rem', fontWeight: 500, color: 'var(--color-text-secondary)', display: 'block', marginBottom: '.35rem' }}>Type</label>
                <select value={form.type} onChange={set('type')} style={{ width: '100%', padding: '.6rem .9rem', border: '1.5px solid var(--color-border)', borderRadius: 'var(--radius-md)', fontSize: '.9rem', background: 'var(--color-surface)' }}>
                  {TYPES.map(t => <option key={t} value={t}>{TYPE_LABELS[t]}</option>)}
                </select>
              </div>
              <Input label="Location" value={form.location} onChange={set('location')} placeholder="Kolkata" />
            </div>
            <Input label="Price/day (₹)  excl. GST" type="number" value={form.price_per_day} onChange={set('price_per_day')} placeholder="500" />
            <Input label="Description" value={form.description} onChange={set('description')} placeholder="Brief description…" />

            {/*  Image section  */}
            <div className={imgStyles.section}>
              <div className={imgStyles.sectionLabel}>
                <span>Images</span>
                <span className={imgStyles.slotCount}>{totalImgCount} / 5</span>
              </div>

              {/* Already-saved images (edit mode) */}
              {savedImages.length > 0 && (
                <div className={imgStyles.savedGrid}>
                  {savedImages.map((url, idx) => {
                    const filename = url.split('/').pop();
                    return (
                      <div key={`saved-${idx}-${filename}`} className={imgStyles.savedThumb}>
                        <img src={resolveImg(url)} alt="bike" />
                        <button
                          className={imgStyles.deleteImgBtn}
                          onClick={() => handleDeleteSavedImage(modal._id, url)}
                          disabled={deletingImg === filename}
                          title="Remove image"
                        >
                          {deletingImg === filename ? '…' : '✕'}
                        </button>
                      </div>
                    );
                  })}
                </div>
              )}

              {/* Pending previews */}
              {selectedFiles.length > 0 && (
                <div className={imgStyles.savedGrid}>
                  {selectedFiles.map((file, i) => (
                    <div key={file.preview} className={`${imgStyles.savedThumb} ${imgStyles.pending}`}>
                      <img src={file.preview} alt="preview" />
                      <span className={imgStyles.pendingBadge}>New</span>
                      <button className={imgStyles.deleteImgBtn} onClick={() => removeSelected(i)} title="Remove">✕</button>
                    </div>
                  ))}
                </div>
              )}

              {/* Drop zone  only show if slots remain */}
              {slotsLeft > 0 && (
                <div
                  className={`${imgStyles.dropZone} ${dragOver ? imgStyles.dragOver : ''}`}
                  onClick={() => fileInputRef.current?.click()}
                  onDragOver={e => { e.preventDefault(); setDragOver(true); }}
                  onDragLeave={() => setDragOver(false)}
                  onDrop={handleDrop}
                >
                  <span className={imgStyles.dropIcon}>📷</span>
                  <p className={imgStyles.dropText}>
                    Drag & drop images here, or <strong>click to browse</strong>
                  </p>
                  <p className={imgStyles.dropHint}>JPEG · PNG · WebP · max 5 MB each · {slotsLeft} slot{slotsLeft !== 1 ? 's' : ''} left</p>
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept="image/jpeg,image/png,image/webp"
                    multiple
                    style={{ display: 'none' }}
                    onChange={e => { addFiles(e.target.files); e.target.value = ''; }}
                  />
                </div>
              )}

              {slotsLeft === 0 && (
                <p className={imgStyles.fullMsg}>✅ Maximum of 5 images reached.</p>
              )}
            </div>

            <Button fullWidth loading={saving || uploadingImgs} onClick={handleSave}>
              {uploadingImgs ? 'Uploading images…' : saving ? 'Saving…' : modal === 'create' ? 'Add bike' : 'Save changes'}
            </Button>
          </div>
        </Modal>
      )}
    </div>
  );
};

export default AdminBikes;
